import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {Menu} from '../interface/menu';
import {CreateMenuRequest} from '../interface/createMenuRequest';
import {UpdateMenuRequest} from '../interface/updateMenuRequest';
import {
  MenuPageResponse,
  MenuBackendResponse,
  PurchaseHistoryPageResponse
} from './menu.interfaces';
import {MenuFilterRequest} from '../interface/menuFilterRequest';
import {MenuMapper} from './menu.mapper';

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
  }

  createMenu(menu: CreateMenuRequest): Observable<{id: number, message: string}> {
    const backendRequest = MenuMapper.mapCreateRequestToBackend(menu);
    return this.http.post<{id: number, message: string}>(`${this.apiUrl}/menu`, backendRequest, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
  }

  getAllMenus(page: number = 0, limit: number = 10): Observable<MenuPageResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', limit.toString());
    return this.http.get<MenuPageResponse>(`${this.apiUrl}/menu`, {params});
  }

  getMenuById(id: number): Observable<Menu> {
    return this.http.get<MenuBackendResponse>(`${this.apiUrl}/menu/${id}`).pipe(
      map(response => MenuMapper.mapBackendResponseToMenu(response))
    );
  }

  updateMenu(id: number, menu: UpdateMenuRequest): Observable<Menu> {
    const backendRequest = MenuMapper.mapUpdateRequestToBackend(menu);
    return this.http.put<MenuBackendResponse>(`${this.apiUrl}/menu/${id}`, backendRequest).pipe(
      map(response => MenuMapper.mapBackendResponseToMenu(response))
    );
  }

  deleteMenu(id: number): Observable<{message: string}> {
    return this.http.delete<{message: string}>(`${this.apiUrl}/menu/${id}`);
  }

  getAllCardUsernames(excludeMenuId?: number): Observable<string[]> {
    let params = new HttpParams();
    if (excludeMenuId != null) {
      params = params.set('excludeMenuId', excludeMenuId.toString());
    }
    return this.http.get<string[]>(`${this.apiUrl}/menu/card-usernames`, {params});
  }

  searchCardUsernames(query: string, excludeMenuId?: number): Observable<string[]> {
    let params = new HttpParams().set('query', query);
    if (excludeMenuId != null) {
      params = params.set('excludeMenuId', excludeMenuId.toString());
    }
    return this.http.get<string[]>(`${this.apiUrl}/menu/card-usernames/search`, {params});
  }

  generateMenuQrCode(id: number): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/menu/${id}/qr-code`, { responseType: 'text' as 'json' });
  }

  searchAllMenus(params: MenuFilterRequest = {}, page: number = 0, size: number = 10): Observable<MenuPageResponse> {
    const httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.post<MenuPageResponse>(
      `${this.apiUrl}/menu/all-menus`,
      params,
      {params: httpParams}
    );
  }

  getPurchaseHistory(params: MenuFilterRequest = {}, page: number = 0, size: number = 10): Observable<PurchaseHistoryPageResponse> {
    const httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', 'purchaseDate,desc');
    return this.http.post<PurchaseHistoryPageResponse>(
      `${this.apiUrl}/menu/purchase-history`,
      params,
      {params: httpParams}
    );
  }
}
